import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Tabs, Tab, Button, Popover, PopoverTrigger, PopoverContent, RangeCalendar, Tooltip } from '@heroui/react';
import Typography from '../../components/typography/Typography';
import { UniversalTable } from '../../components';
import FinanceStatCard from './components/FinanceStatCard';
import staffApi, { CommissionDayStat, CommissionMonthStat } from '../../api/staff';
import { useTranslations } from '../../contexts/translation.context';
import { parseDate, today, getLocalTimeZone } from "@internationalized/date";
import { ResetFiltersIc } from '../../components/icons';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';

type PeriodType = 'day' | 'month';
type CommissionType = 'cpl' | 'revshare';

interface TransactionRow {
  id: string;
  date: string;
  amount: number;
  type: CommissionType;
}

const FinancePage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.finance'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  const [period, setPeriod] = useState<PeriodType>('day');
  const [commissionType, setCommissionType] = useState<CommissionType>('cpl');
  const [isLoading, setIsLoading] = useState(true);
  const [dayStats, setDayStats] = useState<CommissionDayStat[]>([]);
  const [monthStats, setMonthStats] = useState<CommissionMonthStat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // DateRange state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>(null);


  // Вычисление дат для периода (зависит от фильтров)
  const dateRange = useMemo(() => {
    if (selectedDateRange?.start && selectedDateRange?.end) {
      const formatDateToString = (dateValue: any) => {
        const localDate = dateValue.toDate(getLocalTimeZone());
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      return {
        start_date: formatDateToString(selectedDateRange.start),
        end_date: formatDateToString(selectedDateRange.end),
      };
    }

    const now = new Date();

    // Функция для форматирования даты в YYYY-MM-DD (локальное время)
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const endDate = formatLocalDate(now);

    if (period === 'day') {
      // Текущий месяц (с 1 числа до сегодня)
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        start_date: formatLocalDate(startDate),
        end_date: endDate,
      };
    } else {
      // Последние 12 месяцев
      const startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 12);
      return {
        start_date: formatLocalDate(startDate),
        end_date: endDate,
      };
    }
  }, [period, selectedDateRange]);

  // DateRange handlers
  const formatDateRange = (dateRange: any) => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return 'Период';
    }

    const startDate = dateRange.start.toDate(getLocalTimeZone());
    const endDate = dateRange.end.toDate(getLocalTimeZone());

    return `${startDate.toLocaleDateString('ru-RU')} - ${endDate.toLocaleDateString('ru-RU')}`;
  };

  const handleDateRangeChange = (range: any) => {
    setSelectedDateRange(range);
    setCurrentPage(1);
    if (!range) {
      setIsCalendarOpen(false);
    }
  };

  // Загрузка данных
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        ...dateRange,
        page: currentPage,
        page_size: pageSize,
        type: commissionType,
      };

      if (period === 'day') {
        const response = await staffApi.getCommissionStatsByDays(params);
        setDayStats(response.objects || []);
        setTotalCount(response.count || 0);
      } else {
        const response = await staffApi.getCommissionStatsByMonth(params);
        setMonthStats(response.objects || []);
        setTotalCount(response.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching finance data:', err);
      setError(t('finance.errorLoadingData') || 'Failed to load finance data');
    } finally {
      setIsLoading(false);
    }
  }, [period, dateRange, currentPage, pageSize, commissionType, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Обработчик изменения размера страницы
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  // Загрузка сводной статистики для карточек
  const [cplTotal, setCplTotal] = useState(0);
  const [revshareTotal, setRevshareTotal] = useState(0);
  const [overallTotal, setOverallTotal] = useState(0);

  const fetchTotals = useCallback(async () => {
    try {
      const dateParams = {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      };

      const [cplAmount, refShareAmount] = await Promise.all([
        staffApi.getCPLCurrentMonthAmount(dateParams),
        staffApi.getRefShareCurrentMonthAmount(dateParams),
      ]);

      setCplTotal(cplAmount);
      setRevshareTotal(refShareAmount);
      setOverallTotal(cplAmount + refShareAmount);
    } catch (err) {
      console.error('Error fetching totals:', err);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  // Формирование строк таблицы
  const tableData = useMemo(() => {
    if (period === 'day') {
      return dayStats.reverse().map((item, index) => ({
        id: `${item.date}-${index}`,
        date: item.date,
        amount: parseFloat(String(item.amount) || '0'),
        type: commissionType,
      }));
    } else {
      return monthStats.reverse().map((item, index) => ({
        id: `${item.month}-${index}`,
        date: item.month,
        amount: parseFloat(String(item.amount) || '0'),
        type: commissionType,
      }));
    }
  }, [period, dayStats, monthStats, commissionType]);

  // Форматирование даты
  const formatDate = (dateString: string) => {
    try {
      // Для месяца формат может быть "YYYY-MM" или "YYYY-MM-DD"
      let date: Date;

      if (period === 'month') {
        // Если формат YYYY-MM, добавляем -01 для корректного парсинга
        const dateParts = dateString.split('-');
        if (dateParts.length === 2) {
          date = new Date(`${dateString}-01`);
        } else {
          date = new Date(dateString);
        }

        return date.toLocaleDateString('ru-RU', {
          month: 'long',
          year: 'numeric'
        });
      } else {
        date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString;
    }
  };

  // Форматирование суммы
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date range for display in cards - dynamic based on filters
  const formatCardDateRange = useMemo(() => {
    const start = new Date(dateRange.start_date);
    const end = new Date(dateRange.end_date);

    const formatOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    };

    return `${start.toLocaleDateString('ru-RU', formatOptions)} - ${end.toLocaleDateString('ru-RU', formatOptions)}`;
  }, [dateRange]);

  // Колонки таблицы
  const columns = [
    {
      key: 'date',
      name: t('finance.date') || 'Дата',
      sortable: false,
    },
    {
      key: 'amount',
      name: t('finance.amount') || 'Сумма',
      sortable: false,
    },
  ];

  // Рендер ячеек
  const renderCell = (item: TransactionRow, columnKey: string) => {
    switch (columnKey) {
      case 'date':
        return (
          <div className="flex gap-[4px] items-center">
            <svg className="w-[14px] h-[14px]" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.75" y="3.5" width="10.5" height="10.5" rx="1.75" stroke="#71717A" strokeWidth="1.5"/>
              <path d="M4.375 1.75V3.5M9.625 1.75V3.5M1.75 6.125H12.25" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className="text-[12px] leading-[16px] text-[#71717A]">
              {formatDate(item.date)}
            </span>
          </div>
        );
      case 'amount':
        return (
          <span className="text-[14px] font-semibold leading-[16px] text-[#11181C]">
            {formatAmount(item.amount)}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col w-full bg-white">
      {/* Page Header */}
      <div className="px-[24px] py-[16px] pt-[24px]">
        <Typography variant="h5" className="text-[#11181C] font-semibold text-[24px]">
          {t('finance.title') || 'Общая прибыль платформы'}
        </Typography>
      </div>

      {/* Date Range Picker */}
      <div className="px-[24px] pb-[16px] flex items-center gap-[12px]">
        <Popover
          isOpen={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
          placement="bottom-start"
        >
          <PopoverTrigger>
            <Button
              startContent={
                <svg className="w-[20px] h-[20px]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2.5" y="5" width="15" height="15" rx="2.5" stroke="#A8A8B0" strokeWidth="1.5"/>
                  <path d="M6.25 2.5V5M13.75 2.5V5M2.5 8.75H17.5" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              }
              className={`bg-[#F4F4F5] border-none ${selectedDateRange ? 'text-[#11181C]' : 'text-[#71717A]'} rounded-[8px] px-[12px] min-h-[36px] h-[36px] w-fit shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]`}
            >
              {formatDateRange(selectedDateRange)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <RangeCalendar
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              minValue={parseDate("1900-01-01")}
              maxValue={today(getLocalTimeZone())}
              visibleMonths={2}
              className="w-auto"
            />
          </PopoverContent>
        </Popover>

        {selectedDateRange && (
          <Tooltip content="Сбросить фильтр" placement="top">
            <Button
              size="sm"
              className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
              onPress={() => {
                setSelectedDateRange(null);
                setCurrentPage(1);
              }}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Content */}
      <div className="px-[24px] pb-[16px]">
        {/* Stats Grid */}
        <div className="flex gap-[24px] mb-[16px]">
          {/* CPL Card */}
          <div className="flex flex-col gap-[16px] flex-1">
            <Typography variant="body1" className="text-[#11181C] font-medium text-[20px]">
              Доход платформы - CPL
            </Typography>
            <FinanceStatCard
              type="CPL"
              dateRange={formatCardDateRange}
              amount={formatAmount(cplTotal)}
            />
          </div>

          {/* RevShare Card */}
          <div className="flex flex-col gap-[16px] flex-1">
            <Typography variant="body1" className="text-[#11181C] font-medium text-[20px]">
              Доход платформы - RevShare
            </Typography>
            <FinanceStatCard
              type="RevShare"
              dateRange={formatCardDateRange}
              amount={formatAmount(revshareTotal)}
            />
          </div>

          {/* Total Card */}
          <div className="flex flex-col gap-[16px] flex-1">
            <Typography variant="body1" className="text-[#11181C] font-medium text-[20px]">
              Доход платформы - Всего
            </Typography>
            <FinanceStatCard
              type="RevShare + CPL"
              dateRange={formatCardDateRange}
              amount={formatAmount(overallTotal)}
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="px-[24px] pb-[24px]">
        <div className="flex flex-col gap-[24px]">
          {/* Period Selection */}
          <div className="flex flex-col gap-[8px]">
            <p className="text-[14px] font-medium leading-[18px] text-[#71717A]">
              Отображать:
            </p>
            <div className="bg-[#F4F4F5] rounded-[12px] w-fit p-[4px]">
              <Tabs
                selectedKey={period}
                onSelectionChange={(key: React.Key) => {
                  setPeriod(key as PeriodType);
                  setCurrentPage(1);
                }}
                variant="light"
                classNames={{
                  tabList: "gap-[8px] bg-transparent p-0",
                  tab: "px-[12px] py-[4px] text-[14px] font-normal rounded-[8px] data-[selected=true]:bg-white data-[selected=true]:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] data-[selected=true]:text-black text-[#71717A] h-[32px]",
                  cursor: "bg-white rounded-[8px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
                  tabContent: "group-data-[selected=true]:text-black"
                }}
              >
                <Tab key="day" title={t('finance.day') || 'День'} />
                <Tab key="month" title={t('finance.month') || 'Месяц'} />
              </Tabs>
            </div>
          </div>

          {/* Commission Type Tabs */}
          <div className="flex flex-col gap-[8px]">
            <div className="bg-[#F4F4F5] rounded-[12px] w-fit p-[4px]">
              <Tabs
                selectedKey={commissionType}
                onSelectionChange={(key: React.Key) => {
                  setCommissionType(key as CommissionType);
                  setCurrentPage(1);
                }}
                variant="light"
                classNames={{
                  tabList: "gap-[8px] bg-transparent p-0",
                  tab: "px-[12px] py-[4px] text-[14px] font-normal rounded-[8px] data-[selected=true]:bg-white data-[selected=true]:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] data-[selected=true]:text-black text-[#71717A] h-[32px]",
                  cursor: "bg-white rounded-[8px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
                  tabContent: "group-data-[selected=true]:text-black"
                }}
              >
                <Tab key="cpl" title="CPL" />
                <Tab key="revshare" title="RevShare" />
              </Tabs>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-[16px] p-[16px] bg-red-50 border border-red-200 rounded-[12px]">
              <Typography variant="body2" className="text-red-600">
                {error}
              </Typography>
            </div>
          )}

          {/* Transactions Table */}
          <div className="min-h-[480px]">
            {isLoading ? (
              <div className="flex items-center justify-center h-[480px] bg-white rounded-[14px]">
                <Typography variant="body1" className="text-[#71717A]">
                  {t('finance.loading') || 'Loading...'}
                </Typography>
              </div>
            ) : (
              <UniversalTable
                data={tableData}
                columns={columns}
                renderCell={renderCell}
                pagination={{
                  page: currentPage,
                  page_size: pageSize,
                  count: totalCount,
                }}
                currentPage={currentPage}
                currentPageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                emptyContent={
                  <div className="text-center py-[48px]">
                    <Typography variant="body1" className="text-[#71717A]">
                      {t('finance.noData') || 'No transaction data available'}
                    </Typography>
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancePage;
